import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ScrollToTop from "./component/scrollToTop/ScrollToTop";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/scss/app.scss";
import ChatBotModal from "./chat/chatBot";
import axios from "axios";

axios.defaults.withCredentials = true;


// Loading component for Suspense
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "20px",
    }}
  >
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Lazy load home pages
const DwinsoftHome = lazy(() => import("./pages/DwinsoftHome"));

// Lazy load blog pages
const BlogGridCategory = lazy(() => import("./pages/Blog/BlogGridCategory"));
const BlogGridCategoryList = lazy(() =>
  import("./pages/Blog/BlogGridCategoryList")
);
const BlogDetails = lazy(() => import("./pages/Blog/BlogDetails"));
const BlogDetailsx = lazy(() => import("./pages/Blog/BlogDetails_x"));
const BlogSearch = lazy(() => import("./pages/Blog/BlogSearch"));
const BlogCategory = lazy(() => import("./pages/category/Category"));
const BlogArchive = lazy(() => import("./pages/Archive"));
const CategoryDetails = lazy(() => import("./pages/category/CategoryDetails"));

// Lazy load service pages
const ServiceOne = lazy(() => import("./pages/service/ServiceOne"));
const MulesoftConsulting = lazy(() =>
  import("./pages/mule/MulesoftConsulting")
);
const MulesoftImplementation = lazy(() =>
  import("./pages/mule/MulesoftImplementation")
);
const MulesoftDevelopment = lazy(() =>
  import("./pages/mule/MulesoftDevelopment")
);
const MulesoftMigration = lazy(() => import("./pages/mule/MulesoftMigration"));
const MulesoftIntegration = lazy(() =>
  import("./pages/mule/MulesoftIntegration")
);
const ProductionSupport = lazy(() => import("./pages/mule/ProductionSupport"));
const PerformanceTesting = lazy(() =>
  import("./pages/service/PerformanceTesting")
);
const FullstackDevelopment = lazy(() =>
  import("./pages/service/FullstackDevelopment")
);
const Snaplogic = lazy(() => import("./pages/service/Snaplogic"));
const Training = lazy(() => import("./pages/service/Training"));
const MulesoftServices = lazy(() => import("./pages/service/MulesoftServices"));
const SalesforceServices = lazy(() =>
  import("./pages/service/SalesforceServices")
);
const WebDevelopmentServices = lazy(() =>
  import("./pages/service/WebDevelopmentServices")
);
const TestingServices = lazy(() => import("./pages/service/TestingServices"));
const SnaplogicServices = lazy(() =>
  import("./pages/service/SnaplogicServices")
);
const AIServices = lazy(() => import("./pages/service/AIServices"));
const CyberSecurityServices = lazy(() =>
  import("./pages/service/CyberSecurityServices")
);

// Lazy load other pages
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Team = lazy(() => import("./pages/team/Team"));
const TeamDetails = lazy(() => import("./pages/team/TeamDetails"));
const CaseStudy = lazy(() => import("./pages/career/CaseStudy"));
const CaseDetails = lazy(() => import("./pages/career/CaseStudyDetails"));
const Contact = lazy(() => import("./pages/Contact"));
const ErrorPage = lazy(() => import("./pages/404"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const PrivacyPolicy = lazy(() => import("./pages/terms/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/terms/TermsOfUse"));

// Lazy load additional service pages
const AzureServices = lazy(() => import("./pages/service/AzureServices"));
const OdooServices = lazy(() => import("./pages/service/OdooServices"));
const DatabricksServices = lazy(() =>
  import("./pages/service/DatabricksServices")
);
const SnowflakeServices = lazy(() =>
  import("./pages/service/SnowflakeServices")
);
const WorkatoServices = lazy(() => import("./pages/service/WorkatoServices"));
const InforM3 = lazy(() => import("./pages/InforM3"));

// Lazy load admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminJobs = lazy(() => import("./pages/admin/AdminJobs"));
const AdminJobForm = lazy(() => import("./pages/admin/AdminJobForm"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminBlogs = lazy(() => import("./pages/admin/AdminBlogs"));
const AdminApps = lazy(() => import("./pages/admin/AdminApps"));
const AdminCaseStudies = lazy(() => import("./pages/admin/AdminCaseStudies"));
const AdminCaseStudyForm = lazy(() => import("./pages/admin/AdminCaseStudyForm"));
const ProtectedRoute = lazy(() => import("./pages/admin/ProtectedRoute"));

// Lazy load case studies pages
const CaseStudiesPage = lazy(() => import("./pages/casestudies/CaseStudiesPage"));
const CaseStudyDetailPage = lazy(() => import("./pages/casestudies/CaseStudyDetailPage"));

const App = () => {
  return (
    <>
      <ChatBotModal />
      <Router>
        <ScrollToTop>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Admin Routes */}
              <Route path={process.env.PUBLIC_URL + "/management-access/login"} element={<AdminLogin />} />
              <Route path={process.env.PUBLIC_URL + "/management-access"} element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="jobs" element={<AdminJobs />} />
                <Route path="jobs/new" element={<AdminJobForm />} />
                <Route path="jobs/edit/:id" element={<AdminJobForm />} />
                <Route path="jobs/:id/applications" element={<AdminApplications />} />
                <Route path="case-studies" element={<AdminCaseStudies />} />
                <Route path="case-studies/new" element={<AdminCaseStudyForm />} />
                <Route path="case-studies/edit/:id" element={<AdminCaseStudyForm />} />
                <Route path="blogs" element={<AdminBlogs />} />
                <Route path="apps" element={<AdminApps />} />
              </Route>


              <Route
                path={process.env.PUBLIC_URL + "/"}
                element={<DwinsoftHome />}
              />

              {/* Blogs */}
              <Route
                path={process.env.PUBLIC_URL + "/blog/"}
                element={<BlogGridCategory />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/BCL/"}
                element={<BlogGridCategoryList />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/blog/search"}
                element={<BlogSearch />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/category/:slug"}
                element={<BlogCategory />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/archive/:slug"}
                element={<BlogArchive />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/categoty/details/:id"}
                element={<CategoryDetails />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/blog-details/:id"}
                element={<BlogDetails />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/blog-detail/:id"}
                element={<BlogDetailsx />}
              />

              {/* Service */}
              <Route
                path={process.env.PUBLIC_URL + "/services"}
                element={<ServiceOne />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/services/"}
                element={<ServiceOne />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/mulesoft-consulting"}
                element={<MulesoftConsulting />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/mulesoft-implementation"}
                element={<MulesoftImplementation />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/mulesoft-development"}
                element={<MulesoftDevelopment />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/mulesoft-migration"}
                element={<MulesoftMigration />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/mule-b2b-integration"}
                element={<MulesoftIntegration />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/production-support"}
                element={<ProductionSupport />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/performance-testing"}
                element={<PerformanceTesting />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/full-stack-web-development"}
                element={<FullstackDevelopment />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/snaplogic"}
                element={<Snaplogic />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/mulesoft-services"}
                element={<MulesoftServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/salesforce-services"}
                element={<SalesforceServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/web-development-services"}
                element={<WebDevelopmentServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/testing-services"}
                element={<TestingServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/snaplogic-services"}
                element={<SnaplogicServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/ai-services"}
                element={<AIServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/cyber-security-services"}
                element={<CyberSecurityServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/azure-services"}
                element={<AzureServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/odoo-services"}
                element={<OdooServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/databricks-services"}
                element={<DatabricksServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/snowflake-services"}
                element={<SnowflakeServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/workato-services"}
                element={<WorkatoServices />}
              />

              {/* homepage routes */}
              <Route
                path={process.env.PUBLIC_URL + "/workato-implementation"}
                element={<WorkatoServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/workato-support-maintenance"}
                element={<WorkatoServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/snowflake-implementation"}
                element={<SnowflakeServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/snowflake-migration"}
                element={<SnowflakeServices />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/databricks-implementation"}
                element={<DatabricksServices />}
              />
              <Route
                path={
                  process.env.PUBLIC_URL + "/databricks-migration-integration"
                }
                element={<DatabricksServices />}
              />

              {/* Pages  */}
              <Route
                path={process.env.PUBLIC_URL + "/about-us"}
                element={<AboutUs />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/infor-m3"}
                element={<InforM3 />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/team"}
                element={<Team />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/team-details/:slug"}
                element={<TeamDetails />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/case-studies"}
                element={<CaseStudiesPage />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/case-studies/:slug"}
                element={<CaseStudyDetailPage />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/careers"}
                element={<CaseStudy />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/careers/:slug"}
                element={<CaseDetails />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/contact"}
                element={<Contact />}
              />

              <Route
                path={process.env.PUBLIC_URL + "/404"}
                element={<ErrorPage />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/coming-soon"}
                element={<ComingSoon />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/privacy-policy"}
                element={<PrivacyPolicy />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/terms-use"}
                element={<TermsOfUse />}
              />

              <Route
                path={process.env.PUBLIC_URL + "/training-and-certification"}
                element={<Training />}
              />
              <Route
                path={process.env.PUBLIC_URL + "*"}
                element={<ErrorPage />}
              />
            </Routes>
          </Suspense>
        </ScrollToTop>
      </Router>
    </>
  );
};

export default App;
